import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface D3FooterPatternProps {
  width?: number
  height?: number
}

const D3FooterPattern: React.FC<D3FooterPatternProps> = ({ 
  width = typeof window !== 'undefined' ? window.innerWidth : 1920, 
  height = 400
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const animationRef = useRef<number | null>(null)

  useEffect(() => {
    if (!svgRef.current) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // Set SVG dimensions
    svg.attr('width', width).attr('height', height)

    // Create gradient definitions
    const defs = svg.append('defs')

    // Create multiple gradients for different line colors
    const gradients = [
      { id: 'gradient1', colors: ['#3b82f6', '#8b5cf6', 'transparent'] },
      { id: 'gradient2', colors: ['#06b6d4', '#3b82f6', 'transparent'] },
      { id: 'gradient3', colors: ['#8b5cf6', '#ec4899', 'transparent'] },
      { id: 'gradient4', colors: ['#10b981', '#06b6d4', 'transparent'] },
    ]

    gradients.forEach((grad) => {
      const gradient = defs.append('linearGradient')
        .attr('id', grad.id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%')

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', grad.colors[0])
        .attr('stop-opacity', 0.6)

      gradient.append('stop')
        .attr('offset', '50%')
        .attr('stop-color', grad.colors[1])
        .attr('stop-opacity', 0.4)

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', grad.colors[2])
        .attr('stop-opacity', 0)
    })

    // Create line groups
    const lineGroup = svg.append('g').attr('class', 'lines')

    // Generate flowing lines
    const numLines = 8
    const lines: Array<{ path: string; gradient: string; opacity: number }> = []

    for (let i = 0; i < numLines; i++) {
      const yBase = (height / numLines) * i + (height / numLines / 2)
      const amplitude = 30 + Math.random() * 40
      const frequency = 0.001 + Math.random() * 0.002
      const phase = Math.random() * Math.PI * 2
      const gradientId = gradients[i % gradients.length].id
      const opacity = 0.15 + Math.random() * 0.15

      // Create smooth flowing path using sine waves
      const pathData: Array<[number, number]> = []
      const numPoints = 100
      
      for (let j = 0; j <= numPoints; j++) {
        const x = (width / numPoints) * j
        const y = yBase + Math.sin(x * frequency + phase) * amplitude
        pathData.push([x, y])
      }

      // Create smooth curve using d3.line with curveBasis
      const line = d3.line<[number, number]>()
        .x(d => d[0])
        .y(d => d[1])
        .curve(d3.curveBasis)

      const path = line(pathData) || ''

      lines.push({ path, gradient: `url(#${gradientId})`, opacity })
    }

    // Draw lines
    lines.forEach((lineData, i) => {
      lineGroup.append('path')
        .attr('d', lineData.path)
        .attr('fill', 'none')
        .attr('stroke', lineData.gradient)
        .attr('stroke-width', 2)
        .attr('opacity', lineData.opacity)
        .attr('class', `line-${i}`)
    })

    // Create scatterplot group
    const scatterGroup = svg.append('g').attr('class', 'scatterplot')
    
    // Generate scatterplot data points
    const numScatterPoints = 50
    const scatterData: Array<{ x: number; y: number; size: number; color: string; opacity: number }> = []
    
    for (let i = 0; i < numScatterPoints; i++) {
      const x = Math.random() * width
      const y = Math.random() * height
      const size = 3 + Math.random() * 4 // Size between 3-7
      const colorIndex = Math.floor(Math.random() * gradients.length)
      const color = gradients[colorIndex].colors[0]
      const opacity = 0.4 + Math.random() * 0.4 // Opacity between 0.4-0.8 for better visibility
      
      scatterData.push({ x, y, size, color, opacity })
    }
    
    // Draw scatterplot points
    scatterGroup.selectAll('circle')
      .data(scatterData)
      .enter()
      .append('circle')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => d.size)
      .attr('fill', d => d.color)
      .attr('opacity', d => d.opacity)
      .attr('class', 'scatter-point')
    

    // Animate lines and scatterplot
    let time = 0
    let scatterTime = 0
    const animate = () => {
      time += 0.01
      scatterTime += 0.005

      lines.forEach((_, i) => {
        const yBase = (height / numLines) * i + (height / numLines / 2)
        const amplitude = 30 + (i % 3) * 15
        const frequency = 0.001 + (i % 2) * 0.001
        const phase = time + i * 0.5

        const pathData: Array<[number, number]> = []
        const numPoints = 100

        for (let j = 0; j <= numPoints; j++) {
          const x = (width / numPoints) * j
          const y = yBase + Math.sin(x * frequency + phase) * amplitude
          pathData.push([x, y])
        }

        const line = d3.line<[number, number]>()
          .x(d => d[0])
          .y(d => d[1])
          .curve(d3.curveBasis)

        const path = line(pathData) || ''

        svg.select(`.line-${i}`)
          .attr('d', path)
      })

      // Animate scatterplot points
      scatterGroup.selectAll('.scatter-point')
        .data(scatterData)
        .attr('cx', (_d, i) => {
          const baseX = scatterData[i].x
          return baseX + Math.sin(scatterTime + i * 0.1) * 5
        })
        .attr('cy', (_d, i) => {
          const baseY = scatterData[i].y
          return baseY + Math.cos(scatterTime + i * 0.15) * 5
        })
        .attr('opacity', (_d, i) => {
          const baseOpacity = scatterData[i].opacity
          return baseOpacity + Math.sin(scatterTime * 2 + i * 0.2) * 0.1
        })

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    // Handle resize
    const handleResize = () => {
      const newWidth = window.innerWidth
      svg.attr('width', newWidth)
      
      // Recalculate paths with new width
      lines.forEach((_, i) => {
        const yBase = (height / numLines) * i + (height / numLines / 2)
        const amplitude = 30 + (i % 3) * 15
        const frequency = 0.001 + (i % 2) * 0.001
        const phase = time + i * 0.5

        const pathData: Array<[number, number]> = []
        const numPoints = 100

        for (let j = 0; j <= numPoints; j++) {
          const x = (newWidth / numPoints) * j
          const y = yBase + Math.sin(x * frequency + phase) * amplitude
          pathData.push([x, y])
        }

        const line = d3.line<[number, number]>()
          .x(d => d[0])
          .y(d => d[1])
          .curve(d3.curveBasis)

        const path = line(pathData) || ''

        svg.select(`.line-${i}`)
          .attr('d', path)
      })
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [width, height])

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'none', opacity: 0.5, height: '100%' }}
      preserveAspectRatio="none"
    />
  )
}

export default D3FooterPattern

